import { gql, useMutation } from '@apollo/client';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useCallback } from 'react';
import { PlatformUser } from 'src/graphql/entities/platform-user';
import useConversationViewsStore from 'src/store/conversation-views';
import { ClientRaw, ConversationRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { updateCachedConversation } from './conversation';
import { CONVERSATION_FIELDS } from './conversation.fragments';
import { Conversation } from './conversation.types';

const UPDATE_CONVERSATION_MUTATION = gql`
  mutation UpdateConversation($conditions: [ConditionInput], $data: ConversationDataInput) {
    updateConversation(conditions: $conditions, data: $data) {
      ...ConversationFields
    }
  }
  ${CONVERSATION_FIELDS}
`;

export const useConversationUpdate = () => {
  const { showErrorAlert } = useNotifications();
  const { setParticipantPlaceholders, setAssociatedAddressesPlaceholders } =
    useConversationViewsStore();

  const [updateConversationMutation] = useMutation(UPDATE_CONVERSATION_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong updating conversation, please try again');
    },
  });

  const updateConversation = useCallback(
    (conversation: Omit<Conversation, 'requesterId'> & { requesterId?: string | null }) => {
      const patch = removeNotDefinedProperties({
        conversationAssigneeGroupId: conversation.conversationAssigneeGroupId,
        conversationAssigneeId: conversation.conversationAssigneeId,
        type: conversation.type,
        conversationUrgencyId: conversation.urgency,
        subject: conversation.subject,
        conversationStatusId: conversation.conversationStatusId,
        requesterId:
          conversation.requesterId || conversation.requesterId === null
            ? conversation.requesterId
            : conversation.requester?.id,
      });

      const requesterEntity = conversation.requester
        ?.participantEntity as ConversationParticipantEntity;

      // This prevents blinking of the conversation requestor when its data is missing right after the update.
      // We perform an optimistic update of the conversation participants map using Zustand,
      // since adjusting the Apollo cache is complex due to the use of different queries
      // for requestors and participants, each with distinct filters. Although Apollo normalizes the cache,
      // neither fragments nor cache modification methods will work correctly in our scenario.
      if (requesterEntity === ConversationParticipantEntity.Client) {
        const { participantIdentity } = conversation.requester;

        setParticipantPlaceholders({
          [participantIdentity]: {
            id: participantIdentity,
            clientId: participantIdentity,
            partnerId: conversation.partnerId,
            firstName: conversation?.requesterFullName ?? '',
            lastName: '',
          } as ClientRaw,
        });
      } else if (requesterEntity === ConversationParticipantEntity.PlatformUser) {
        const { participantIdentity } = conversation.requester;

        setParticipantPlaceholders({
          [participantIdentity]: {
            id: participantIdentity,
            userId: participantIdentity,
            firstName: conversation?.requesterFullName ?? '',
            lastName: '',
          } as PlatformUser,
        });
      }

      if (conversation.associatedAddresses && conversation.conversationId) {
        const { associatedAddresses } = conversation;

        setAssociatedAddressesPlaceholders({
          [conversation.conversationId]: associatedAddresses,
        });
      }

      const optimistic = {
        ...conversation,
        ...patch,
      };

      return updateConversationMutation({
        variables: {
          conditions: [
            {
              attribute: 'conversationId',
              operator: '=',
              value: conversation.conversationId,
            },
          ],
          data: patch,
        },
        update: (
          cache,
          {
            data: response,
          }: {
            data?: {
              updateConversation: Partial<ConversationRaw>[];
            };
          },
        ) => {
          const updated = response?.updateConversation?.[0];
          if (!updated) return updated;

          const conv = { ...optimistic, ...updated };

          if (conversation.associatedAddresses.length === 0) {
            conv.associatedAddresses = conversation.associatedAddresses;
          }

          updateCachedConversation({
            cache,
            props: conv as Partial<Conversation>,
            conversationId: conversation.conversationId,
            extraFields: {
              requesterId: () => conv.requesterId,
              requester: (_, { toReference }) =>
                toReference({
                  __typename: 'ConversationParticipant',
                  id: conv.requesterId,
                }),
            },
          });
        },
      });
    },
    [
      updateConversationMutation,
      setParticipantPlaceholders,
      setAssociatedAddressesPlaceholders,
    ],
  );

  return { updateConversation };
};
